// import { IGetAllSearchPropertiesResponse } from '@backend/types/responseTypes'
// import { User } from '@backend/zod/index'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { INotification } from 'src/components/elements/header/components/notifications/item/NotificationItem'
import { IRequest } from 'src/components/elements/request-card/RequestCard'
import {
  IProperty,
  IContactReveal,
  IOffer,
  IUserRating,
  IPropertyChange,
  IFavoriteFeedItem,
} from 'src/interfaces/IProperty'
import { socket } from 'src/socket'
import { RootState } from './store'
import { NotificationChannel } from 'src/interfaces/types'
import { ISession } from 'src/components/pages/profile/sections/sessions/item/ActiveSessionsItem'
import {
  MapPropertiesResponse,
  MapPropertiesParams,
  CitiesResponse,
  DistrictsResponse,
} from 'src/types/map'
import i18n from 'src/i18n'
import { xhrUpload } from './xhrUpload'

type PropertyTab = {
  id: string
  label: string
  count: number
}

type PropertyTabsResponse = {
  tabs: PropertyTab[]
  totalCount: number
}

// A card in the profile "Мої пропозиції" (my own listings) section.
export interface IMyProperty {
  id: string
  title: string
  listingType: string
  propertyType: string
  price: { amount: number; currency: string }
  currency: string
  images: string[]
  status: 'ACTIVE' | 'PENDING' | 'REJECTED' | 'COMPLETED'
  createdAt: string
  viewsCount: number
  requestsCount: number
  // Owner mini-dashboard extras (roadmap 23.1)
  contactsCount?: number
  rejectionReason?: string | null
  // Paid promotion state (monetization)
  promotedUntil?: string | null
  isPromoted?: boolean
}

// Monetization: paid TOP placement
export interface IPromotionPlan {
  id: string
  key: string
  nameUk: string
  nameRu: string
  nameEn: string
  durationDays: number
  priceKop: number
  active: boolean
  order: number
}

export interface IPromotionPreview {
  totalActiveInCity: number
  promotedInCity: number
  estimatedPosition: number
  isPromoted: boolean
  promotedUntil: string | null
}

export type PaymentStatus =
  'CREATED' | 'PROCESSING' | 'SUCCESS' | 'FAILURE' | 'EXPIRED' | 'REVERSED'

export interface IPayment {
  id: string
  propertyId: string
  provider: 'MONOBANK' | 'MOCK'
  status: PaymentStatus
  amountKop: number
  pageUrl?: string | null
  failureReason?: string | null
  createdAt: string
  appliedAt?: string | null
  plan?: { nameUk: string; nameRu: string; nameEn: string; durationDays: number } | null
  property?: { id: string; addressString: string; images?: string[] } | null
}

export interface ICreatePropertyPayload {
  description: string
  addressSection: {
    settlement: string
    district?: string
    street?: string
    houseNumber?: string
    apartment?: string
  }
  listingSection: {
    listingType: 'SALE' | 'RENT'
    propertyType: 'APARTMENT' | 'HOUSE' | 'COMMERCIAL' | 'LAND'
    ownershipType: 'OWNER' | 'REALTOR'
  }
  parametersSection: {
    rooms: number
    floor: number
    totalFloors: number
    totalArea: number
    livingArea: number
    kitchenArea: number
  }
  location: {
    lat: number
    lon: number
  }
  price: {
    amount: number
    currency: 'UAH' | 'USD'
  }
  // Contacts-on-request toggle (roadmap 41 B1); omitted → backend default (on).
  autoRevealContacts?: boolean
}
export type NotificationType =
  'SYSTEM' | 'SPECIAL_OFFERS' | 'NEW_MESSAGE' | 'NEW_PROPERTY' | 'PRICE_CHANGE'

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}`,
    credentials: 'include', // для відправки cookie з токеном сесії
    prepareHeaders: (headers) => {
      // Add the current language to all requests
      const currentLanguage = i18n.language || 'uk'
      headers.set('X-Language', currentLanguage)
      return headers
    },
  }),
  tagTypes: [
    'propertySearch',
    'User',
    'PropertyRequests',
    'Properties',
    'Chats',
    'Auction',
    'NotificationPreferences',
    'Sessions',
    'PasswordChange',
    'DiiaStatus',
    'Agreements',
    'PropertyContacts',
    'Offers',
    'Reviews',
    'Payments',
  ],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    sendPhoneVerification: builder.mutation<
      { message: string; verificationSid: string },
      { phoneNumber: string; purpose?: string }
    >({
      query: (data) => ({
        url: '/auth/send-phone-verification',
        method: 'POST',
        body: data,
      }),
    }),
    verifyPhone: builder.mutation<
      { message: string; user?: any; requiresRegistration?: boolean },
      { phoneNumber: string; code: string; purpose?: string }
    >({
      query: (data) => ({
        url: '/auth/verify-phone',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    register: builder.mutation<
      { message: string },
      { firstName: string; lastName: string; emailOrPhone: string; password: string }
    >({
      query: (credentials) => ({
        url: '/auth/register',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User'],
    }),
    forgotPassword: builder.mutation<{ message: string }, { emailOrPhone: string }>({
      query: (body) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<
      { message: string },
      { token: string; password: string } | { phoneNumber: string; code: string; password: string }
    >({
      query: (body) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    getNews: builder.query<
      {
        items: { id: string; slug: string; title: string; content: string; updatedAt: string }[]
      },
      string
    >({
      query: (lang) => `/content/news?lang=${lang}`,
    }),
    getBanners: builder.query<
      { items: { id: string; title: string; content: string; type: string; position: string }[] },
      void
    >({
      query: () => '/content/banners',
    }),
    submitInquiry: builder.mutation<
      { message: string; id: string },
      {
        name: string
        email: string
        phone?: string
        subject: string
        message: string
        requestType?: string
        propertyId?: string
      }
    >({
      query: (body) => ({
        url: '/inquiries',
        method: 'POST',
        body,
      }),
    }),
    getPropertyTabs: builder.query<PropertyTabsResponse, undefined>({
      query: () => '/property/tabs',
      providesTags: ['PropertyRequests'],
    }),
    makeFavorite: builder.mutation({
      query: ({
        propertyId,
        propertySearchId,
        isFavorite,
      }: {
        propertyId: string
        propertySearchId: string
        isFavorite: boolean
      }) => ({
        url: `/property/${propertyId}/favorite`,
        params: { propertySearchId, isFavorite },
        method: 'POST',
      }),
      async onQueryStarted(
        { propertyId, propertySearchId, isFavorite },
        { dispatch, queryFulfilled, getState },
      ) {
        // Получаем все существующие кеши
        const state = getState() as RootState
        // Фильтруем кеши, чтобы найти те, которые содержат propertySearchId
        const pages = Object.keys(state.api.queries)
          .filter((key) => key.startsWith('getProperties'))
          .filter((key) => {
            const argsString = key.split('(')[1]?.split(')')[0]
            try {
              const args = JSON.parse(argsString || '{}')
              return args.propertySearchId === propertySearchId
            } catch {
              return false
            }
          })

        const patchResults = pages.map((key) => {
          const queryArgs = JSON.parse(key.split('(')[1]?.split(')')[0] || '{}')
          const patchResult = dispatch(
            api.util.updateQueryData('getProperties', queryArgs, (draft) => {
              const property = draft.properties.find((p) => p.id === propertyId)
              if (property) {
                property.isFavorite = isFavorite
              }
            }),
          )
          return patchResult
        })

        try {
          await queryFulfilled
        } catch {
          // В случае ошибки отменяем все оптимистичные обновления
          patchResults.forEach((patchResult) => patchResult.undo())
        }
      },
    }),
    getPropertyRequests: builder.query<
      { items: IRequest[]; totalPages: number },
      { propertyId: string; currentPage: number; limit: number; responded?: boolean }
    >({
      query: ({ propertyId, currentPage, limit, responded }) => ({
        url: `/property/${propertyId}/requests`,
        params: {
          page: currentPage,
          limit,
          sort: 'createdAt desc',
          responded,
        },
      }),
      providesTags: (result, error, { propertyId, currentPage }) => [
        { type: 'PropertyRequests', id: `${propertyId}-page-${currentPage}` },
        'PropertyRequests',
      ],
      keepUnusedDataFor: 0, // Don't cache old page data
    }),
    approveProperty: builder.mutation({
      query: ({
        propertyId,
        propertySearchId,
      }: {
        propertyId: string
        propertySearchId: string
      }) => ({
        url: `/property/${propertyId}/approve`,
        params: { propertySearchId },
        method: 'POST',
      }),
      invalidatesTags: ['PropertyRequests'],
    }),
    withdrawProperty: builder.mutation({
      query: ({
        propertyId,
        propertySearchId,
      }: {
        propertyId: string
        propertySearchId: string
      }) => ({
        url: `/property/${propertyId}/approve`,
        params: { propertySearchId },
        method: 'DELETE',
      }),
      invalidatesTags: ['PropertyRequests'],
    }),
    getUser: builder.query<any, undefined>({
      query: () => '/user/me', // маршрут для отримання даних про користувача
      providesTags: ['User'],
    }),
    getUserProfileStats: builder.query<
      {
        createdAt: string
        language: string
        offersCount: number
        requestsCountAll: number
        requestsCountActive: number
        noPasswordSet: boolean
      },
      undefined
    >({
      query: () => '/user/profile',
      providesTags: ['User', 'PasswordChange'],
    }),
    updateUserProfile: builder.mutation<
      { success: boolean; user: any },
      Partial<{ firstName: string; lastName: string; email: string; phone: string; city: string }>
    >({
      query: (body) => ({ url: '/user/update-profile', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    changePassword: builder.mutation<
      { success: boolean },
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({ url: '/user/change-password', method: 'POST', body }),
      invalidatesTags: ['PasswordChange'],
    }),
    uploadAvatar: builder.mutation<{ success: boolean; avatar: string }, FormData>({
      query: (form) => ({ url: '/user/avatar', method: 'POST', body: form }),
      invalidatesTags: ['User'],
    }),
    updatePrivacy: builder.mutation<
      { success: boolean; showOnlineStatus: boolean },
      { showOnlineStatus: boolean }
    >({
      query: (body) => ({ url: '/user/privacy', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    sendChatMessage: builder.mutation({
      query: ({
        chatId,
        recipientId,
        text,
      }: {
        chatId?: string
        recipientId?: string
        text: string
      }) => ({
        url: `/chat`,
        method: 'POST',
        body: { text, chatId, recipientId },
      }),
      invalidatesTags: ['Chats'], // Refresh chat list in messenger
    }),
    getOrCreateSupportChat: builder.mutation<
      { id: string; name: string; avatar: string | null; isSupport: boolean },
      void
    >({
      query: () => ({ url: '/chat/support', method: 'POST' }),
    }),
    changePropertyMode: builder.mutation({
      query: (propertyMode) => ({
        url: '/user/changePropertyMode',
        method: 'POST',
        body: { mode: propertyMode },
      }),
      async onQueryStarted(propertyMode, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData('getUser', undefined, (draft) => {
            Object.assign(draft, {
              propertyMode: propertyMode,
              isOwner: propertyMode === 'LOOKING_FOR_PROPERTY' ? false : true,
            })
          }),
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getProperties: builder.query<
      { properties: IProperty[]; totalPages: number },
      {
        propertySearchId: string
        search?: string
        page: number
        sort?: string
        limit?: number
        onlyFavorites?: boolean
        responded?: boolean
      }
    >({
      query: ({ propertySearchId, search, page, sort, limit, onlyFavorites, responded }) => ({
        url: `/property/`,
        params: { propertySearchId, search, page, sort, limit, onlyFavorites, responded },
      }),
      providesTags: ['propertySearch'],
    }),
    // The caller's own listings for the profile "Мої пропозиції" section.
    // Daily view buckets for the owner's sparkline (roadmap 28.4)
    getMyPropertyViewsDaily: builder.query<
      { days: { date: string; count: number }[] },
      { id: string; days?: number }
    >({
      query: ({ id, days = 14 }) => ({
        url: `/property/my/${id}/views-daily`,
        params: { days },
      }),
    }),
    getMyProperties: builder.query<{ items: IMyProperty[] }, undefined>({
      query: () => ({
        url: `/property/my`,
      }),
      providesTags: ['Properties'],
    }),
    getPropertySearchById: builder.query<any, string>({
      query: (id) => ({
        url: `/property-search/${id}`,
      }),
      providesTags: ['propertySearch'],
    }),
    getPropertySearches: builder.query<any, { onlyFavorites?: boolean } | undefined>({
      query: (args) => ({
        url: `/property-search/`,
        params: args,
      }),
      providesTags: ['propertySearch'],
    }),
    createPropertySearch: builder.mutation({
      query: (args) => ({
        url: '/property-search/',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['propertySearch'],
    }),
    deletePropertySearch: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/property-search/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['propertySearch'],
    }),
    // Stamps lastViewedAt so the "new matches" badge on the request card resets.
    markPropertySearchViewed: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/property-search/${id}/viewed`,
        method: 'POST',
      }),
      invalidatesTags: ['propertySearch'],
    }),
    createPropertyWithImages: builder.mutation<
      { id: string },
      {
        property: ICreatePropertyPayload
        images: File[]
        // the share of the photos sent (xhrUpload), for the publish progress
        onProgress?: (share: number) => void
      }
    >({
      queryFn: async ({ property, images, onProgress }) => {
        const formData = new FormData()
        formData.append('property', JSON.stringify(property))
        images.forEach((img) => formData.append('images', img))
        const result = await xhrUpload('/property/with-images', formData, onProgress)
        return 'error' in result ? result : { data: result.data as { id: string } }
      },
      invalidatesTags: ['propertySearch', 'Properties'],
    }),
    createProperty: builder.mutation<{ id: string }, ICreatePropertyPayload>({
      query: (args) => ({
        url: '/property/',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['propertySearch'],
    }),
    deleteProperty: builder.mutation<void, string>({
      query: (id) => ({
        url: `/property/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['propertySearch', 'PropertyRequests'],
    }),
    updateProperty: builder.mutation<
      any,
      // The full-page edit (roadmap 40) sends the same sections as create;
      // the backend whitelists what is actually editable.
      { id: string; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/property/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['propertySearch', 'PropertyRequests', 'Properties'],
    }),
    updatePropertyImages: builder.mutation<
      any,
      {
        id: string
        images: File[]
        keepImages: string[]
        imageOrder?: string[]
        onProgress?: (share: number) => void
      }
    >({
      queryFn: async ({ id, images, keepImages, imageOrder, onProgress }) => {
        const formData = new FormData()
        formData.append('keepImages', JSON.stringify(keepImages))
        // Final photo order (A4.1): existing URLs plus `__new__<k>` placeholders
        // for the k-th uploaded file, so old and new photos can be interleaved.
        if (imageOrder) formData.append('imageOrder', JSON.stringify(imageOrder))
        images.forEach((img) => formData.append('images', img))
        return xhrUpload(`/property/${id}/images`, formData, onProgress)
      },
      invalidatesTags: ['Properties'],
    }),
    markAsRead: builder.mutation<any, { notificationIds: string[] }>({
      query: ({ notificationIds }) => ({
        url: '/notifications/markAsRead',
        body: { notificationIds },
        method: 'POST',
      }),
      async onQueryStarted({ notificationIds }, { dispatch, queryFulfilled, getState }) {
        // getNotifications is an infinite query cached per `limit` arg, so the
        // header dropdown (limit 10) and the Notifications page (limit 20) are
        // DIFFERENT cache entries. Patch every live entry, not a hardcoded one,
        // otherwise items never visually mark read on the page that is mounted.
        const state: any = getState()
        const patches = Object.values(state.api.queries)
          .filter((entry: any) => entry?.endpointName === 'getNotifications')
          .map((entry: any) =>
            dispatch(
              api.util.updateQueryData('getNotifications', entry.originalArgs, (draft) => {
                draft.pages
                  .flat()
                  .map((n) => n.notifications)
                  .flat()
                  .forEach((n) => {
                    if (notificationIds.includes(n.id)) {
                      n.read = true
                    }
                  })
              }),
            ),
          )

        const patchUnreadCount = dispatch(
          api.util.updateQueryData('getUnreadCount', undefined, (count) => {
            // Защита от отрицательных значений
            return Math.max(0, count - notificationIds.length)
          }),
        )
        try {
          // 2. Wait for the mutation to succeed
          await queryFulfilled
        } catch {
          // 3. Rollback in case of failure
          patches.forEach((p) => p.undo())
          patchUnreadCount.undo()
        }
      },
    }),
    markAllAsRead: builder.mutation<any, void>({
      query: () => ({ url: '/notifications/markAllAsRead', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        const state: any = getState()
        const patches = Object.values(state.api.queries)
          .filter((entry: any) => entry?.endpointName === 'getNotifications')
          .map((entry: any) =>
            dispatch(
              api.util.updateQueryData('getNotifications', entry.originalArgs, (draft) => {
                draft.pages
                  .flat()
                  .map((n) => n.notifications)
                  .flat()
                  .forEach((n) => {
                    n.read = true
                  })
              }),
            ),
          )
        const patchUnreadCount = dispatch(
          api.util.updateQueryData('getUnreadCount', undefined, () => 0),
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => p.undo())
          patchUnreadCount.undo()
        }
      },
    }),
    deleteNotification: builder.mutation<any, { id: string; wasUnread: boolean }>({
      query: ({ id }) => ({ url: `/notifications/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, wasUnread }, { dispatch, queryFulfilled, getState }) {
        const state: any = getState()
        const patches = Object.values(state.api.queries)
          .filter((entry: any) => entry?.endpointName === 'getNotifications')
          .map((entry: any) =>
            dispatch(
              api.util.updateQueryData('getNotifications', entry.originalArgs, (draft) => {
                draft.pages.forEach((page) => {
                  page.notifications = page.notifications.filter((n) => n.id !== id)
                })
              }),
            ),
          )
        const patchUnreadCount = wasUnread
          ? dispatch(
              api.util.updateQueryData('getUnreadCount', undefined, (count) =>
                Math.max(0, count - 1),
              ),
            )
          : undefined
        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => p.undo())
          patchUnreadCount?.undo()
        }
      },
    }),
    getUnreadCount: builder.query({
      query: () => '/notifications/unreadCount',
      // The unread counter is bumped HERE (a single cache entry), not inside
      // getNotifications — which has one cache entry per `limit` (header:10,
      // page:20), so incrementing there double-counted every event.
      async onCacheEntryAdded(_arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const onNotification = () => {
          updateCachedData((count: number) => count + 1)
        }
        try {
          await cacheDataLoaded
          socket.on('notification', onNotification)
        } catch {
          // cache entry removed before load resolved
        }
        await cacheEntryRemoved
        socket.off('notification', onNotification)
      },
    }),
    getNotifications: builder.infiniteQuery<
      { notifications: INotification[]; nextPage?: string },
      { limit: number },
      { after?: string }
    >({
      query: ({ pageParam, queryArg }) => {
        return {
          url: '/notifications/',
          params: { ...queryArg, ...pageParam },
        }
      },
      infiniteQueryOptions: {
        initialPageParam: {},
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams, args) => {
          if (!lastPage.nextPage) {
            return undefined
          }

          return { limit: args.limit, after: lastPage.nextPage }
        },
      },
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // Named handler so teardown removes ONLY this entry's listener;
        // socket.off('notification') with no handler removed every consumer's
        // (including the header dropdown's), killing live push for the session.
        // The unread counter is handled by getUnreadCount, not here.
        const onNotification = (data: INotification) => {
          updateCachedData((draft) => {
            draft.pages[0].notifications.unshift(data)
          })
        }
        try {
          await cacheDataLoaded
          socket.on('notification', onNotification)
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        socket.off('notification', onNotification)
      },
    }),
    changeLanguage: builder.mutation<any, { language: string }>({
      query: (args) => ({
        url: '/user/changeLanguage',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['propertySearch'],
    }),
    autocomplete: builder.query<google.maps.places.AutocompletePrediction[], any>({
      // Используем GET, все параметры сериализуем в query
      query: ({ input, sessionToken, locationRestriction, types }) => {
        const params: Record<string, string> = {
          input,
        }
        if (sessionToken) params.sessionToken = sessionToken as string
        if (locationRestriction) params.locationRestriction = locationRestriction
        if (types && types.length) params.types = types.join(',')
        return {
          url: '/google/autocomplete',
          params,
        }
      },
    }),
    placeDetails: builder.query<any, { place_id: string; sessionToken?: string }>({
      query: ({ place_id, sessionToken }) => ({
        url: `/google/place/${place_id}`,
        params: {
          ...(sessionToken ? { sessionToken } : {}),
        },
      }),
    }),
    getChatMessagesSearch: builder.query<
      {
        chatUser: {
          user: {
            id: string
            firstName: string
            lastName: string
            avatar: string | null
          }
        }
        id: string
        createdAt: Date
        content: string
      }[],
      { chatId: string; search: string }
    >({
      query: ({ chatId, search }) => ({
        url: `/chat/${chatId}/messagesSearch`,
        params: { search },
      }),
    }),
    approveRequestEmail: builder.mutation({
      query: (body: { email: string }) => ({
        url: '/auth/approve-request-email',
        method: 'POST',
        body,
      }),
    }),
    verifyRequestEmail: builder.query({
      query: (params: { token: string; email: string }) => ({
        url: '/auth/verify-request-email',
        method: 'GET',
        params,
      }),
    }),
    sendChangeEmailCode: builder.mutation<{ success: boolean; token: string }, { email: string }>({
      query: (body) => ({ url: '/user/email/send-code', method: 'POST', body }),
    }),
    verifyChangeEmailCode: builder.mutation<
      { success: boolean; user: any },
      { email: string; code: string }
    >({
      query: (body) => ({ url: '/user/email/verify-code', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    // isRequestEmailApproved: builder.query({
    //   query: (token: string) => ({
    //     url: '/auth/is-request-email-approved',
    //     method: 'GET',
    //     params: { token },
    //   }),
    // }),
    getPropertyById: builder.query<IProperty, string>({
      query: (id) => `/property/${id}`,
      providesTags: ['propertySearch', 'Auction', 'PropertyContacts'],
    }),
    // Contacts-on-request (roadmap 41 B1). A viewer asks to see the owner's
    // phone; auto-reveal listings unlock it at once, otherwise it queues for
    // the owner. Invalidating PropertyContacts refetches the gated detail.
    revealContact: builder.mutation<
      { status: 'PENDING' | 'REVEALED'; phone: string | null },
      string
    >({
      query: (id) => ({ url: `/property/${id}/reveal-contact`, method: 'POST' }),
      transformResponse: (r: { data: { status: 'PENDING' | 'REVEALED'; phone: string | null } }) =>
        r.data,
      invalidatesTags: ['PropertyContacts'],
    }),
    // The owner approves a pending request → REVEALED (auto-reveal off).
    approveContactReveal: builder.mutation<
      { status: 'REVEALED' },
      { propertyId: string; revealId: string }
    >({
      query: ({ propertyId, revealId }) => ({
        url: `/property/${propertyId}/reveal/${revealId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['PropertyContacts'],
    }),
    // Owner-only "хто відкрив контакти" log for a listing.
    getContactReveals: builder.query<{ items: IContactReveal[] }, string>({
      query: (id) => `/property/${id}/contact-reveals`,
      providesTags: ['PropertyContacts'],
    }),
    // Structured bargaining (roadmap 41 B2). Propose a price, respond to one,
    // and read the caller's negotiation thread for a listing.
    proposeOffer: builder.mutation<
      unknown,
      { propertyId: string; amount: number; currency: string; toUserId?: string }
    >({
      query: ({ propertyId, ...body }) => ({
        url: `/property/${propertyId}/offer`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Offers'],
    }),
    acceptOffer: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/offer/${id}/accept`, method: 'POST' }),
      invalidatesTags: ['Offers'],
    }),
    declineOffer: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/offer/${id}/decline`, method: 'POST' }),
      invalidatesTags: ['Offers'],
    }),
    counterOffer: builder.mutation<unknown, { id: string; amount: number }>({
      query: ({ id, amount }) => ({
        url: `/offer/${id}/counter`,
        method: 'POST',
        body: { amount },
      }),
      invalidatesTags: ['Offers'],
    }),
    // The proposer edits their own pending offer (B2 rework); the old one is
    // kept as history and a fresh pending offer is chained on the server.
    updateOffer: builder.mutation<unknown, { id: string; amount: number }>({
      query: ({ id, amount }) => ({
        url: `/offer/${id}/update`,
        method: 'POST',
        body: { amount },
      }),
      invalidatesTags: ['Offers'],
    }),
    getPropertyOffers: builder.query<{ items: IOffer[] }, string>({
      query: (id) => `/property/${id}/offers`,
      providesTags: ['Offers'],
    }),
    // Ratings & reviews (roadmap 41 H1). Leave/edit a review for a user and
    // read their public score + reviews for the agent block.
    createReview: builder.mutation<
      unknown,
      {
        userId: string
        propertyId: string
        rating: number
        comment?: string
        anonymous?: boolean
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `/user/${userId}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Reviews', 'propertySearch'],
    }),
    getUserReviews: builder.query<IUserRating, string>({
      query: (userId) => `/user/${userId}/reviews`,
      providesTags: ['Reviews'],
    }),
    reportReview: builder.mutation<
      unknown,
      { reviewId: string; reason: string; description?: string }
    >({
      query: ({ reviewId, ...body }) => ({
        url: `/user/reviews/${reviewId}/report`,
        method: 'POST',
        body,
      }),
    }),
    // Change feed (roadmap 41 C3): a listing's public change history and the
    // caller's favourites news feed (recent changes to hearted listings).
    getPropertyChanges: builder.query<{ items: IPropertyChange[] }, string>({
      query: (id) => `/property/${id}/changes`,
    }),
    getFavoritesFeed: builder.query<{ items: IFavoriteFeedItem[] }, void>({
      query: () => `/property/favorites/feed`,
      // Favouriting changes which listings feed in, so refetch with the hearts.
      providesTags: ['propertySearch'],
    }),
    // Conversation moderation (roadmap 41 F2): report a chat message. Lands in
    // the admin moderation queue; nothing on the client to invalidate.
    reportMessage: builder.mutation<
      unknown,
      { messageId: string; reason: string; description?: string }
    >({
      query: ({ messageId, ...body }) => ({
        url: `/chat/messages/${messageId}/report`,
        method: 'POST',
        body,
      }),
    }),
    // Other active listings by the same seller (roadmap 41 B3).
    getRelatedProperties: builder.query<
      {
        id: string
        images: string[]
        price: { amount: number; currency: string }
        addressString?: string
        propertyType: string
        listingType: string
        isPromoted: boolean
      }[],
      string
    >({
      query: (id) => `/property/${id}/related`,
      providesTags: ['Properties'],
    }),
    // Personalised recommendations from the caller's view history (roadmap 41).
    getRecommendedProperties: builder.query<
      {
        id: string
        images: string[]
        price: { amount: number; currency: string }
        addressString?: string
        propertyType: string
        listingType: string
        isPromoted: boolean
      }[],
      void
    >({
      query: () => `/property/recommended`,
      providesTags: ['Properties'],
    }),
    // Median-of-comparables price context (roadmap 27); 204 → undefined data.
    getPriceContext: builder.query<
      {
        comparableCount: number
        median: number
        target: number
        diffPercent: number
        unit: 'perArea' | 'total'
        currency: string
      } | null,
      string
    >({
      query: (id) => `/property/${id}/price-context`,
    }),
    getPriceHint: builder.query<
      {
        comparableCount: number
        medianTotal: number
        medianPerArea: number | null
        currency: string
      } | null,
      { propertyType: string; listingType: string; city: string }
    >({
      query: (params) => ({ url: '/property/price-hint', params }),
    }),
    createBid: builder.mutation({
      query: (body: { propertyId: string; amount: number }) => ({
        url: `/auctions/property/${body.propertyId}/bid`,
        method: 'POST',
        body: { amount: body.amount },
      }),
      invalidatesTags: ['Auction'],
    }),
    getAuctionInfo: builder.query<
      {
        propertyId: string
        currentPrice?: number
        minStep?: number
        maxStep?: number
        endDate?: string
        isAuctionEnded?: boolean
        bids: {
          id: string
          amount: number
          createdAt: string
          user: { id: string; firstName: string; lastName: string; avatar?: string }
        }[]
      },
      string
    >({
      query: (propertyId) => `/auctions/property/${propertyId}`,
      providesTags: ['Auction'],
    }),
    changeNotificationPreferences: builder.mutation({
      query: (body: { channel: string; type: string }) => ({
        url: '/notification-preferences/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotificationPreferences'],
      async onQueryStarted({ channel, type }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData(
            'getNotificationPreferences',
            undefined,
            (notificationPreferences) => {
              const data: {
                webTypes: NotificationType[]
                telegramTypes: NotificationType[]
                emailTypes: NotificationType[]
              } = {
                webTypes: notificationPreferences.webTypes,
                telegramTypes: notificationPreferences.telegramTypes,
                emailTypes: notificationPreferences.emailTypes,
              }

              switch (channel) {
                case NotificationChannel.WEB:
                  data.webTypes = notificationPreferences.webTypes.includes(type)
                    ? notificationPreferences.webTypes.filter(
                        (item: NotificationType) => item !== type,
                      )
                    : [...notificationPreferences.webTypes, type]
                  break
                case NotificationChannel.TELEGRAM:
                  data.telegramTypes = notificationPreferences.telegramTypes.includes(type)
                    ? notificationPreferences.telegramTypes.filter(
                        (item: NotificationType) => item !== type,
                      )
                    : [...notificationPreferences.telegramTypes, type]
                  break
                case NotificationChannel.EMAIL:
                  data.emailTypes = notificationPreferences.emailTypes.includes(type)
                    ? notificationPreferences.emailTypes.filter(
                        (item: NotificationType) => item !== type,
                      )
                    : [...notificationPreferences.emailTypes, type]
                  break
              }

              Object.assign(notificationPreferences, data)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getNotificationPreferences: builder.query({
      query: () => '/notification-preferences/',
      providesTags: ['NotificationPreferences'],
    }),
    connectTelegram: builder.mutation<{ success: boolean; message: string }, { chatId: string }>({
      query: (body) => ({
        url: '/notification-preferences/telegram/connect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User', 'NotificationPreferences'],
    }),
    disconnectTelegram: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: '/notification-preferences/telegram/disconnect',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'NotificationPreferences'],
    }),
    getTelegramStatus: builder.query<{ connected: boolean; chatId: string | null }, void>({
      query: () => '/notification-preferences/telegram/status',
      providesTags: ['User'],
    }),
    get2FAStatus: builder.query<{ enabled: boolean; telegramConnected: boolean }, void>({
      query: () => '/two-factor/status',
      providesTags: ['User'],
    }),
    enable2FA: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: '/two-factor/enable',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    disable2FA: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: '/two-factor/disable',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    send2FACode: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: '/two-factor/send-code',
        method: 'POST',
      }),
    }),
    verify2FACode: builder.mutation<{ success: boolean; message: string }, { code: string }>({
      query: (body) => ({
        url: '/two-factor/verify-code',
        method: 'POST',
        body,
      }),
    }),
    // Diia endpoints
    getDiiaStatus: builder.query<
      {
        success: boolean
        data: { verified: boolean; verifiedAt?: string }
      },
      void
    >({
      query: () => '/diia/status',
      providesTags: ['DiiaStatus'],
      keepUnusedDataFor: 0, // Don't cache - always fetch fresh
    }),
    verifyWithDiiaBarcode: builder.mutation<
      {
        success: boolean
        message: string
        data?: { verified: boolean; verifiedAt?: string }
      },
      { barcode: string }
    >({
      query: (body) => ({
        url: '/diia/verify',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DiiaStatus', 'User'],
    }),
    revokeDiiaVerification: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: '/diia/revoke',
        method: 'POST',
      }),
      invalidatesTags: ['DiiaStatus', 'User'],
    }),
    deleteAccount: builder.mutation({
      query: () => ({
        url: '/user/deleteAccount',
        method: 'DELETE',
      }),
    }),
    getSession: builder.query<ISession[], undefined>({
      query: () => '/user/sessions',
      providesTags: ['Sessions'],
    }),
    deleteSession: builder.mutation({
      query: ({ sessionId }) => ({
        url: `/user/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['Sessions'],
    }),
    deleteOthersSession: builder.mutation({
      query: () => ({
        url: '/user/sessions/others',
        method: 'DELETE',
      }),
      invalidatesTags: ['Sessions'],
    }),
    // Map endpoints
    getMapProperties: builder.query<MapPropertiesResponse, MapPropertiesParams>({
      query: (params) => ({
        url: '/property/map',
        params,
      }),
    }),
    getCities: builder.query<CitiesResponse, void>({
      query: () => '/property/cities',
    }),
    getDistricts: builder.query<DistrictsResponse, { city: string; propertyIds?: string }>({
      query: ({ city, propertyIds }) => ({
        url: '/property/districts',
        params: { city, ...(propertyIds && { propertyIds }) },
      }),
    }),
    generateAgreement: builder.mutation<{ fileUrl: string; fileName: string }, any>({
      query: (data) => ({
        url: '/agreement/generate',
        method: 'POST',
        body: data,
      }),
    }),
    // Browser push (roadmap 28.2)
    getPushPublicKey: builder.query<{ enabled: boolean; publicKey: string | null }, void>({
      query: () => '/notification-preferences/push/public-key',
    }),
    subscribePush: builder.mutation<{ success: boolean }, { subscription: unknown }>({
      query: (body) => ({
        url: '/notification-preferences/push/subscribe',
        method: 'POST',
        body,
      }),
    }),
    unsubscribePush: builder.mutation<{ success: boolean }, { endpoint: string }>({
      query: (body) => ({
        url: '/notification-preferences/push/unsubscribe',
        method: 'POST',
        body,
      }),
    }),
    // Persisted contracts (roadmap 24.2)
    getMyAgreements: builder.query<
      {
        items: {
          id: string
          kind: 'LAND_SALE' | 'RENTAL'
          status: 'DRAFT' | 'SENT' | 'SIGNED_BY_INITIATOR' | 'SIGNED_BY_COUNTERPARTY' | 'COMPLETED'
          fileName: string
          propertyId: string | null
          initiatorId: string
          counterpartyId: string | null
          createdAt: string
        }[]
      },
      void
    >({
      query: () => ({ url: '/agreement/my' }),
      providesTags: ['Agreements'],
    }),
    sendAgreement: builder.mutation<
      { item: { id: string; status: string } },
      { id: string; counterpartyId?: string }
    >({
      query: ({ id, counterpartyId }) => ({
        url: `/agreement/${id}/send`,
        method: 'POST',
        body: counterpartyId ? { counterpartyId } : {},
      }),
      invalidatesTags: ['Agreements'],
    }),
    getPublicStats: builder.query<{ totalUsers: number }, void>({
      query: () => ({
        url: '/public-stats',
        method: 'GET',
      }),
    }),
    getExchangeRate: builder.query<{ currency: string; rate: number; date: string }, string>({
      query: (currency) => ({
        url: '/exchange-rate',
        params: { currency },
      }),
    }),
    getAllExchangeRates: builder.query<
      { rates: { UAH: number; EUR: number; USD: number }; date: string },
      void
    >({
      query: () => ({
        url: '/exchange-rate/all',
      }),
    }),
    // Monetization: paid TOP placement (docs/product/monetization-2026-08-21.md)
    getPromotionPlans: builder.query<
      { enabled: boolean; testMode: boolean; plans: IPromotionPlan[] },
      void
    >({
      query: () => ({ url: '/monetization/plans' }),
      transformResponse: (r: {
        data: { enabled: boolean; testMode: boolean; plans: IPromotionPlan[] }
      }) => r.data,
    }),
    getPromotionPreview: builder.query<IPromotionPreview, string>({
      query: (propertyId) => ({ url: `/monetization/preview/${propertyId}` }),
      transformResponse: (r: { data: IPromotionPreview }) => r.data,
      providesTags: ['Payments'],
    }),
    promoteProperty: builder.mutation<
      { paymentId: string; payUrl?: string; instant?: boolean },
      { propertyId: string; planId: string }
    >({
      query: (body) => ({ url: '/monetization/promote', method: 'POST', body }),
      transformResponse: (r: { data: { paymentId: string; payUrl?: string; instant?: boolean } }) =>
        r.data,
      // Instant (test-mode) purchases change promotedUntil everywhere the
      // owner sees their listing; the details page listens on PropertyContacts.
      invalidatesTags: ['Properties', 'Payments', 'PropertyContacts'],
    }),
    getMyPayments: builder.query<
      { payments: IPayment[]; total: number; page: number; limit: number },
      { page?: number; limit?: number } | void
    >({
      query: (args) => ({ url: '/monetization/payments/my', params: args || undefined }),
      transformResponse: (r: {
        data: { payments: IPayment[]; total: number; page: number; limit: number }
      }) => r.data,
      providesTags: ['Payments'],
    }),
    getPaymentById: builder.query<IPayment, string>({
      query: (id) => ({ url: `/monetization/payments/${id}` }),
      transformResponse: (r: { data: IPayment }) => r.data,
      providesTags: ['Payments'],
    }),
  }),
})

export const {
  useGetMyPropertiesQuery,
  useGetMyPropertyViewsDailyQuery,
  useGetPushPublicKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
  useGetSessionQuery,
  useDeleteSessionMutation,
  useLoginMutation,
  useSendPhoneVerificationMutation,
  useVerifyPhoneMutation,
  useGetUserQuery,
  useGetUserProfileStatsQuery,
  useCreatePropertyWithImagesMutation,
  useDeletePropertyMutation,
  useUpdatePropertyMutation,
  useUpdatePropertyImagesMutation,
  useGetPropertyTabsQuery,
  useGetPropertyRequestsQuery,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
  useGetNewsQuery,
  useGetBannersQuery,
  useSubmitInquiryMutation,
  useChangePropertyModeMutation,
  useCreatePropertySearchMutation,
  useGetPropertiesQuery,
  useGetPropertySearchesQuery,
  useGetPropertySearchByIdQuery,
  useCreatePropertyMutation,
  useDeletePropertySearchMutation,
  useMarkPropertySearchViewedMutation,
  useGetNotificationsInfiniteQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useGetUnreadCountQuery,
  useChangeLanguageMutation,
  useLazyAutocompleteQuery,
  usePlaceDetailsQuery,
  useLazyPlaceDetailsQuery,
  useApprovePropertyMutation,
  useWithdrawPropertyMutation,
  useMakeFavoriteMutation,
  useSendChatMessageMutation,
  useGetOrCreateSupportChatMutation,
  useGetChatMessagesSearchQuery,
  useApproveRequestEmailMutation,
  useVerifyRequestEmailQuery,
  useGetPropertyByIdQuery,
  useRevealContactMutation,
  useApproveContactRevealMutation,
  useGetContactRevealsQuery,
  useProposeOfferMutation,
  useAcceptOfferMutation,
  useDeclineOfferMutation,
  useCounterOfferMutation,
  useUpdateOfferMutation,
  useGetPropertyOffersQuery,
  useCreateReviewMutation,
  useGetUserReviewsQuery,
  useReportReviewMutation,
  useGetPropertyChangesQuery,
  useGetFavoritesFeedQuery,
  useReportMessageMutation,
  useGetRelatedPropertiesQuery,
  useGetRecommendedPropertiesQuery,
  useGetPriceContextQuery,
  useGetPriceHintQuery,
  useCreateBidMutation,
  useGetAuctionInfoQuery,
  useChangeNotificationPreferencesMutation,
  useGetNotificationPreferencesQuery,
  useConnectTelegramMutation,
  useDisconnectTelegramMutation,
  useGetTelegramStatusQuery,
  useGet2FAStatusQuery,
  useEnable2FAMutation,
  useDisable2FAMutation,
  useSend2FACodeMutation,
  useVerify2FACodeMutation,
  useGetDiiaStatusQuery,
  useVerifyWithDiiaBarcodeMutation,
  useRevokeDiiaVerificationMutation,
  useDeleteAccountMutation,
  useUpdateUserProfileMutation,
  useChangePasswordMutation,
  useUploadAvatarMutation,
  useUpdatePrivacyMutation,
  useSendChangeEmailCodeMutation,
  useVerifyChangeEmailCodeMutation,
  useGetMapPropertiesQuery,
  useGetCitiesQuery,
  useGetDistrictsQuery,
  useGenerateAgreementMutation,
  useGetMyAgreementsQuery,
  useSendAgreementMutation,
  useGetPublicStatsQuery,
  useGetExchangeRateQuery,
  useLazyGetExchangeRateQuery,
  useGetAllExchangeRatesQuery,
  useGetPromotionPlansQuery,
  useGetPromotionPreviewQuery,
  usePromotePropertyMutation,
  useGetMyPaymentsQuery,
  useGetPaymentByIdQuery,
} = api
